// The positions an EFP will write, in the same review-and-edit grid the imports use.
//
// Replaces the narrow read-only panel: the legs are the point of the screen, so they get
// a full-width table, and volume and price are editable because a confirmation sometimes
// needs a correction before it is posted.
import HotTable from '@handsontable/react-wrapper';
import { Box } from '@mui/material';
import { ColumnSettings } from 'handsontable/settings';
import { useCallback, useMemo, useRef } from 'react';
import { PositionType, Side } from '../../../../Generated/Commodity-Management-Api';
import { EfpLeg, EfpPlan } from './EfpPositionPlan';

const HANDSONTABLE_LICENSE_KEY = '29ae0-f35ad-79c41-2472d-2c70d';

/** Month/year, the form every other date on these screens takes. */
function monthYear(date: { month?: number; year?: number } | undefined): string {
    if (!date?.month || !date?.year) return '-';
    return `${date.month}/${date.year}`;
}

export interface EfpLegGridProps {
    plan: EfpPlan;
    /** Volume and price are editable; everything else is derived. */
    onChangeLeg?: (index: number, changes: Partial<Pick<EfpLeg, 'volume' | 'price'>>) => void;
    height?: number | string;
}

interface LegRow {
    position: string;
    location: string;
    budgetDate: string;
    contractDate: string;
    volume: number;
    unit: string;
    price: number;
    allocated: string;
    detail: string;
}

export default function EfpLegGrid(props: EfpLegGridProps) {
    const { plan, onChangeLeg, height = 260 } = props;
    const hotRef = useRef<any>(null);

    const rows = useMemo<LegRow[]>(() => {
        const legRows = plan.legs.map(leg => ({
            position: `${leg.side === Side.Buy ? 'Buy' : 'Sell'} ${leg.positionType.toLowerCase()}`,
            location: leg.description.replace(/^.*? at /, '').replace(/ @.*$/, ''),
            budgetDate: monthYear(leg.budgetDate),
            contractDate: monthYear(leg.contractDate),
            volume: leg.volume,
            unit: String(leg.unit),
            price: leg.price,
            allocated: leg.allocate ? 'Yes' : 'No',
            detail: leg.description,
        }));

        if (plan.basisReduction) {
            legRows.push({
                position: 'Reduce existing basis',
                location: '-',
                budgetDate: monthYear(plan.legs[0]?.budgetDate),
                contractDate: monthYear(plan.legs[0]?.contractDate),
                volume: plan.basisReduction.newVolume,
                unit: String(plan.legs[0]?.unit ?? ''),
                price: plan.basisReduction.price,
                allocated: '-',
                detail: `Position ${plan.basisReduction.positionId}: `
                    + `${plan.basisReduction.currentVolume.toLocaleString()} → `
                    + `${plan.basisReduction.newVolume.toLocaleString()}`,
            });
        }

        return legRows;
    }, [plan]);

    const columns = useMemo<ColumnSettings[]>(() => [
        { data: 'position', readOnly: true, width: 150 },
        { data: 'location', readOnly: true, width: 130 },
        { data: 'budgetDate', readOnly: true, width: 100 },
        { data: 'contractDate', readOnly: true, width: 100 },
        {
            data: 'volume',
            type: 'numeric',
            numericFormat: { pattern: '0,0.[00]' },
            // The reduction row is derived from the legs above it.
            readOnly: !onChangeLeg,
            width: 110,
        },
        { data: 'unit', readOnly: true, width: 70 },
        {
            data: 'price',
            type: 'numeric',
            numericFormat: { pattern: '0,0.[0000]' },
            readOnly: !onChangeLeg,
            width: 110,
        },
        { data: 'allocated', readOnly: true, width: 90 },
        { data: 'detail', readOnly: true, width: 320 },
    ], [onChangeLeg]);

    const afterChange = useCallback((changes: any, source: string) => {
        if (!changes || source === 'loadData' || !onChangeLeg) return;

        for (const [rowIndex, prop, , next] of changes) {
            // The basis reduction row is not a leg.
            if (rowIndex >= plan.legs.length) continue;
            if (prop !== 'volume' && prop !== 'price') continue;

            const value = Number(next);
            if (isNaN(value)) continue;
            onChangeLeg(rowIndex, { [prop]: value } as Partial<Pick<EfpLeg, 'volume' | 'price'>>);
        }
    }, [onChangeLeg, plan.legs.length]);

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            <HotTable
                ref={hotRef}
                data={rows}
                columns={columns}
                colHeaders={['Position', 'Location', 'Budget Date', 'Contract Date', 'Volume', 'Unit', 'Price', 'Allocated', 'Detail']}
                rowHeaders={false}
                width="100%"
                height={height}
                stretchH="last"
                rowHeights={32}
                columnHeaderHeight={40}
                minSpareRows={0}
                manualColumnResize
                afterChange={afterChange}
                licenseKey={HANDSONTABLE_LICENSE_KEY}
            />
        </Box>
    );
}
